import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    type StepInteractionNameValues,
} from 'dibs-tracking';
import { CATALOG_LIST_TRIGGERS, CATALOG_TYPES } from 'dibs-personalized/exports/tracking.ts';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';

const { EVENT_WISHLIST } = eventNameConstants;
const { INTERACTION_TYPE_ITEM_FAVORITING } = interactionTypeConstants;
const {
    STEP_INTERACTION_ITEM_ADDED,
    STEP_INTERACTION_ITEM_REMOVED,
    STEP_INTERACTION_ITEM_ADDED_TO_FOLDER,
    STEP_INTERACTION_ITEM_FOLDER_CLICKED,
    STEP_INTERACTION_ITEM_FOLDER_CREATED,
    STEP_INTERACTION_ITEM_REMOVED_FROM_FOLDER,
    STEP_INTERACTION_UNAUTH_SAVE_CLICK,
} = stepInteractionConstants;

const SIDEBAR = 'SIDEBAR';
const SIMILAR_ITEM_GRID = 'SIMILAR_ITEM_GRID';
const SIMILAR_ITEM_TOP = 'SIMILAR_ITEM_TOP';
const SEARCH_BROWSE = 'SEARCH_BROWSE';
const ZOOM_MODAL = 'ZOOM_MODAL';
const MOBILE_NAV = 'MOBILE_NAV';
const AUTO_EMAIL = 'AUTO_EMAIL';
const SIMILAR_SOLD = 'SIMILAR_SOLD';
const PERSONALIZED_COLLECTION_GLOBAL = CATALOG_TYPES.GLOBAL;
const PERSONALIZED_COLLECTION_SPECIFIC = CATALOG_TYPES.SPECIFIC;
const PERSONALIZED_COLLECTION_COMPLEMENTARY = CATALOG_TYPES.COMPLEMENTARY;
const MORE_RELATED_ITEMS = 'MORE_RELATED_ITEMS';

export const locations = {
    SIDEBAR,
    SIMILAR_ITEM_GRID,
    SIMILAR_ITEM_TOP,
    SEARCH_BROWSE,
    ZOOM_MODAL,
    MOBILE_NAV,
    AUTO_EMAIL,
    SIMILAR_SOLD,
    PERSONALIZED_COLLECTION_GLOBAL,
    PERSONALIZED_COLLECTION_SPECIFIC,
    PERSONALIZED_COLLECTION_COMPLEMENTARY,
    FEATURED_RELATED_ITEMS: 'pdp featured related items',
    MORE_RELATED_ITEMS,
    RECENTLY_FAVORITED_ITEMS: 'pdp recently favorited items',
    MORE_FROM_SELLER: 'pdp more from seller',
    PERSONALIZATION_MODULE: 'pdp personalization module',
    YOU_MAY_ALSO_LIKE: 'pdp you may also like',
} as const;

export const authLabels = {
    [locations.SIDEBAR]: 'save item - product page',
    [locations.SIMILAR_ITEM_GRID]: 'save item - product page - you may also like',
    [locations.SIMILAR_ITEM_TOP]: 'save item - product page - top you may also like',
    [locations.SEARCH_BROWSE]: 'save item - search/browse',
    [locations.MOBILE_NAV]: 'save item - product nav',
} as const;

const heartLabels: Record<(typeof locations)[keyof typeof locations], string> = {
    [locations.SIDEBAR]: 'PDP',
    [locations.SIMILAR_ITEM_GRID]: 'PDP - you may also like',
    [locations.SIMILAR_ITEM_TOP]: 'PDP - top you may also like',
    [locations.MOBILE_NAV]: 'PDP - mobile navigation',
    [locations.SEARCH_BROWSE]: 'search/browse',
    [locations.AUTO_EMAIL]: 'PDP - auto email',
    [locations.SIMILAR_SOLD]: 'similar sold item',
    [locations.PERSONALIZED_COLLECTION_GLOBAL]: CATALOG_LIST_TRIGGERS.GLOBAL,
    [locations.PERSONALIZED_COLLECTION_COMPLEMENTARY]: CATALOG_LIST_TRIGGERS.COMPLEMENTARY,
    [locations.PERSONALIZED_COLLECTION_SPECIFIC]: CATALOG_LIST_TRIGGERS.SPECIFIC,
    [locations.FEATURED_RELATED_ITEMS]: 'PDP - featured related items',
    [locations.MORE_RELATED_ITEMS]: 'PDP - explore more related items',
    [locations.RECENTLY_FAVORITED_ITEMS]: 'pdp recently favorited items',
    [locations.MORE_FROM_SELLER]: 'pdp more from seller',
    [locations.PERSONALIZATION_MODULE]: 'pdp personalization module',
    [locations.YOU_MAY_ALSO_LIKE]: 'pdp you may also like',
    [locations.ZOOM_MODAL]: '',
};
export type FavoritesLocation = keyof typeof heartLabels;

const folderLabels = {
    [locations.SEARCH_BROWSE]: 'search/browse',
    [locations.SIMILAR_SOLD]: 'similar sold item',
} as const;

function trackHeartEvent(
    location: FavoritesLocation,
    action: string | undefined,
    itemId: string | null | undefined,
    step_interaction_name: StepInteractionNameValues,
    isSponsoredItem: boolean | null = null,
    isNewListing: boolean = false,
    catalogID?: string | null
): void {
    trackEvent(
        {
            action,
            category: INTERACTION_TYPE_ITEM_FAVORITING,
            label: heartLabels[location],
            isInteractiveEvent: true,
            additional: {
                ...(isSponsoredItem !== null ? { dimension141: isSponsoredItem } : {}),
                ...(itemId ? { itemId } : {}),
                ...(catalogID ? { catalogID } : {}),
                dimension142: isNewListing,
            },
            eventName: EVENT_WISHLIST,
            interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
            step_interaction_name,
            trigger: heartLabels[location],
        },
        null
    );
}

type TrackHeartEventActionProps = {
    location: FavoritesLocation;
    itemPk?: string | null;
    isSponsored?: boolean | null;
    isNewListing?: boolean;
    catalogID?: string | null;
};

export const trackHeartFavorited = ({
    location,
    itemPk,
    isSponsored,
    isNewListing,
    catalogID,
}: TrackHeartEventActionProps): void =>
    trackHeartEvent(
        location,
        'item added',
        itemPk,
        STEP_INTERACTION_ITEM_ADDED,
        isSponsored,
        isNewListing,
        catalogID
    );

export const trackHeartUnfavorited = ({
    location,
    itemPk,
    isSponsored,
    isNewListing,
    catalogID,
}: TrackHeartEventActionProps): void =>
    trackHeartEvent(
        location,
        'item removed',
        itemPk,
        STEP_INTERACTION_ITEM_REMOVED,
        isSponsored,
        isNewListing,
        catalogID
    );

export const trackHeartUnauthClick = ({
    location,
    itemId,
}: {
    location: FavoritesLocation;
    itemId: string | null | undefined;
}): void =>
    trackEvent({
        eventName: EVENT_WISHLIST,
        interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
        step_interaction_name: STEP_INTERACTION_UNAUTH_SAVE_CLICK,
        trigger: heartLabels[location],
        isInteractiveEvent: true,
        ...(itemId && { additional: { itemId } }),
    });

function trackFolderEvent(
    step_interaction_name: StepInteractionNameValues,
    location: FavoritesLocation,
    noteIncluded?: boolean,
    itemPk?: string
): void {
    const trigger = hasKey(folderLabels, location) ? folderLabels[location] : 'pdp';
    const hasNoteIncluded = noteIncluded ? 1 : 0;
    trackEvent(
        {
            action: step_interaction_name,
            label: trigger,
            category: INTERACTION_TYPE_ITEM_FAVORITING,
            value: hasNoteIncluded,
            isInteractiveEvent: true,
            additional: itemPk ? { itemPk } : {},
            eventName: EVENT_WISHLIST,
            interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
            step_interaction_name,
            trigger,
            step_type: hasNoteIncluded,
        },
        null
    );
}

const trackFolderClick = (location: FavoritesLocation): void =>
    trackFolderEvent(STEP_INTERACTION_ITEM_FOLDER_CLICKED, location);
const trackItemRemoved = (location: FavoritesLocation): void =>
    trackFolderEvent(STEP_INTERACTION_ITEM_REMOVED_FROM_FOLDER, location);
const trackItemAdded = (
    location: FavoritesLocation,
    noteIncluded: boolean,
    itemPk: string
): void => {
    const trigger = heartLabels[location];
    const hasNoteIncluded = noteIncluded ? 1 : 0;
    trackEvent(
        {
            action: 'item added into folder',
            label: trigger,
            category: INTERACTION_TYPE_ITEM_FAVORITING,
            value: hasNoteIncluded,
            isInteractiveEvent: true,
            additional: itemPk ? { itemPk } : {},
            eventName: EVENT_WISHLIST,
            interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
            step_interaction_name: STEP_INTERACTION_ITEM_ADDED_TO_FOLDER,
            trigger,
            step_type: hasNoteIncluded,
        },
        null
    );
};
const trackFolderCreated = (location: FavoritesLocation, noteIncluded: boolean): void =>
    trackFolderEvent(STEP_INTERACTION_ITEM_FOLDER_CREATED, location, noteIncluded);

export const getFavoritesLocationByCatalogType = (
    catalogType: string | null | undefined
): FavoritesLocation => {
    if (catalogType === CATALOG_TYPES.SPECIFIC) {
        return locations.PERSONALIZED_COLLECTION_SPECIFIC;
    } else if (catalogType === CATALOG_TYPES.COMPLEMENTARY) {
        return locations.PERSONALIZED_COLLECTION_COMPLEMENTARY;
    } else {
        return locations.PERSONALIZED_COLLECTION_GLOBAL;
    }
};

export const folderTrackingHooks = {
    trackFolderClick,
    trackItemRemoved,
    trackItemAdded,
    trackFolderCreated,
};
